import { getAssetUrl } from "../api/config";
import { normalizeRating, parseBookingSlotDate } from "./bookingModels";

export const PAGE_CONSULTATION_TYPE = {
  video: "video",
  inPerson: "in-person",
};

export const API_CONSULTATION_TYPE = {
  video: "Online",
  inPerson: "Offline",
};

export const toApiType = (pageType) =>
  pageType === PAGE_CONSULTATION_TYPE.inPerson
    ? API_CONSULTATION_TYPE.inPerson
    : API_CONSULTATION_TYPE.video;

export const fromApiType = (apiType) =>
  apiType === API_CONSULTATION_TYPE.inPerson
    ? PAGE_CONSULTATION_TYPE.inPerson
    : PAGE_CONSULTATION_TYPE.video;

export const doctorSlots = (doctor, apiType) =>
  (apiType === API_CONSULTATION_TYPE.video ? doctor.onlineSlots : doctor.offlineSlots) || [];

export const matchesApiType = (doctor, apiType) => {
  const ctype = doctor.consultation_type;
  if (!ctype) return true;
  if (apiType === API_CONSULTATION_TYPE.video) {
    return ctype === "Online" || ctype === "Both";
  }
  return ctype === "Offline" || ctype === "Both";
};

const toYMD = (dateStr) => {
  const slot = parseBookingSlotDate(dateStr);
  if (slot) return slot.date;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isSameDay = (a, b) => toYMD(a) === toYMD(b);

export const formatNextAvailable = (slots) => {
  const sorted = (slots || [])
    .filter((s) => s?.date && !s.isExpired)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const first = sorted[0];
  if (!first) return { label: "—", isToday: false };

  const slotDate = new Date(first.date);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  let dateLabel;
  if (isSameDay(slotDate, today)) dateLabel = "Today";
  else if (isSameDay(slotDate, tomorrow)) dateLabel = "Tomorrow";
  else {
    dateLabel = slotDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }

  const label = first.timeSlot ? `${dateLabel}, ${first.timeSlot}` : dateLabel;
  return { label, isToday: isSameDay(slotDate, today) };
};

export const formatDoctorName = (name) => {
  const n = name || "Doctor";
  return /^dr\.?\s/i.test(n) ? n : `Dr. ${n}`;
};

export const formatExperienceYears = (years) => {
  if (years == null || years === "") return "—";
  const s = String(years);
  return s.toLowerCase().includes("year") ? s : `${s} Years`;
};

export const mapToDoctorCard = (raw, apiType) => {
  const booking = raw.bookingSlot
    ? parseBookingSlotDate(raw.bookingSlot.date, raw.bookingSlot.timeSlot)
    : null;
  const slots = doctorSlots(raw, apiType);
  const fromSlots = formatNextAvailable(slots);
  const today = new Date();
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const label = booking?.label || fromSlots.label;
  const isToday = booking ? booking.date === todayYmd : fromSlots.isToday;
  const experience =
    raw.experience ?? raw.yearsOfExperience ?? raw.experience_years ?? null;
  const ratingAverage = normalizeRating(raw.rating ?? raw.averageRating);
  const reviewCount =
    raw.reviews ?? raw.reviewCount ?? raw.totalReviews ?? null;

  return {
    id: raw._id,
    name: formatDoctorName(raw.name),
    specialty: raw.specialization || "General Physician",
    profilePictureUrl: getAssetUrl(raw.image) || "/logo.png",
    ratingAverage,
    reviewCount,
    experienceYears: experience,
    description: raw.description || raw.bio || raw.about || raw.qualification || "",
    nextAvailableDatetime: label,
    primaryAction: { style: isToday ? "filled" : "outlined" },
    _raw: raw,
  };
};

export const unwrapList = (data) =>
  Array.isArray(data) ? data : data?.doctors || data?.data || [];

export const doctorMatchesSpecialty = (raw, specialtyFilter) => {
  if (!specialtyFilter) return true;
  const q = specialtyFilter.toLowerCase();
  const spec = (raw.specialization || raw.category || "").toLowerCase();
  const cats = (raw.categories || []).map((c) => String(c).toLowerCase());
  return spec.includes(q) || cats.some((c) => c.includes(q));
};

export const doctorMatchesSearch = (raw, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    raw.name?.toLowerCase().includes(q) ||
    raw.specialization?.toLowerCase().includes(q) ||
    raw.description?.toLowerCase().includes(q) ||
    raw.address?.toLowerCase().includes(q)
  );
};
